import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiCode, FiLoader, FiTerminal } from 'react-icons/fi';
import Admin2 from 'layouts/Admin2';
import PageHeading from 'admin/components/PageHeading';
import { authorizedCommands } from './commands';
import useAuthStore from 'store/authStore';
import { Permission } from 'utils/server-constants/permissions';
import useCommandRuns from './useCommandRuns';
import CommandCard from './CommandCard';
import CommandActivity from './CommandActivity';
import CommandReview from './CommandReview';
import RecoveryPanel from './RecoveryPanel';
import './commandCenter.css';
import useOverlayState from 'components/Overlays/useOverlayState';

export default function CommandCenter() {
    const auth = useAuthStore();
    const commands = authorizedCommands(auth);
    const [showDetails, setShowDetails] = useState(false);
    const review = useOverlayState();
    const selection = review.value;
    const { running, results, activity, run } = useCommandRuns();
    const requestRun = (command, hours, trigger) => {
        if (running) return;
        if (command.review) review.open({ command, hours, trigger });
        else run(command);
    };
    const closeReview = review.close;
    const confirmRun = () => {
        const { command, hours } = selection;
        closeReview();
        run(command, hours);
    };
    return <Admin2 fullWidth>
        <div className="command-center">
            <PageHeading title="Command center" description="Keep your library running smoothly. Manage application settings, currency rates and order recovery."
                actions={auth.hasPermissions([Permission.ViewConfiguration]) && <Link to="/configurations" className="portal-button">View configurations<FiArrowUpRight aria-hidden="true" /></Link>} />
            <div className="command-toolbar"><span className="command-toolbar-label"><FiTerminal aria-hidden="true" /><strong>{commands.length}</strong> available commands</span>
                <label className="command-details-toggle"><FiCode aria-hidden="true" /><span>Show technical details</span><input type="checkbox" checked={showDetails} onChange={(event) => setShowDetails(event.target.checked)} /><span className="command-switch" aria-hidden="true" /></label>
            </div>
            {running && <div className="command-running-banner" role="status"><FiLoader className="command-spin" aria-hidden="true" /><span><strong>{commands.find((command) => command.id === running)?.title} is running.</strong> Other commands will be available when it finishes.</span></div>}
            <div className="command-workspace">
                <div className="command-main">
                    {[['System maintenance', 'Keep application settings and currency rates up to date.']].filter(([group]) => commands.some((command) => command.group === group)).map(([group, description]) => <section className="command-group" key={group} aria-labelledby={`command-group-${group.split(' ')[0]}`}>
                        <header className="command-group-heading"><div><h2 id={`command-group-${group.split(' ')[0]}`}>{group}</h2><p>{description}</p></div><span>{commands.filter((command) => command.group === group).length} commands</span></header>
                        <div className="command-card-grid">{commands.filter((command) => command.group === group).map((command) => <CommandCard key={command.id} command={command} result={results[command.id]} running={running} showDetails={showDetails} onRun={requestRun} />)}</div>
                    </section>)}
                    {commands.length === 0 && <p role="status">No commands are available with your current permissions.</p>}
                    <div className="command-help"><FiTerminal aria-hidden="true" /><p><strong>Choose a command to get started.</strong> Each tool runs individually. Results stay visible here during this visit.</p></div>
                </div>
                <div className="command-side">{commands.some((command) => command.id === 'reconcile') && <RecoveryPanel command={commands.find((command) => command.id === 'reconcile')} result={results.reconcile} running={running} showDetails={showDetails} onRun={requestRun} />}
                    <CommandActivity activity={activity} /></div>
            </div>
            {selection && <CommandReview visible={review.visible} onExited={review.onExited} selection={selection} onClose={closeReview} onConfirm={confirmRun} />}
        </div>
    </Admin2>;
}
